import { Component, OnInit, Inject, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, catchError, concatMap, debounceTime, throwError } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { WebsocketAwareNeptuneService } from '../../../../Bootstrap/services/websocket-aware-neptune.service';
import { HttpVerbsEnum } from '../../../../api/shared/enums/http-verbs.enum';
import { LoginApi } from '../../../../api/login/login.api';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { validateEmail } from '../../../Misc/helpers/formatting/email.helper';
import { ConfigService } from '../../../../Core/Config/services/config.service';

export function emailValidator(): ValidatorFn {
    return (control: AbstractControl) => {
        const invalid = !validateEmail(control.value);
        return invalid ? {email: invalid} : null;
    };
}

@Component({
    selector: 'agpr-ghost-in-dialog',
    templateUrl: 'ghost-in-dialog.component.html',
    styleUrls: ['ghost-in-dialog.component.scss'],
    standalone: false
})
export class GhostInDialogComponent extends AbstractDialogComponent implements OnInit {

    loading: boolean = false;
    invalidGhostIn: boolean = false;
    ghostInForm: FormGroup;
    showErrorMessages: boolean = false;

    ghostInUser: any;

    private subscriptions: Subscription[] = [];

    readonly id = 'ghostInDialog';
    readonly buttonTypesEnum = ButtonTypesEnum;

    classicGhostInRoute = '';

    @ViewChild('classicForm') classicForm: ElementRef;

    constructor(
        coreDialogService: CoreDialogService,
        private router: Router,
        private websocketAwareNeptuneService: WebsocketAwareNeptuneService,
        private currentUserStateService: CurrentUserStateService,
        private loginApi: LoginApi,
        public configService: ConfigService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {
        super(coreDialogService, 'GhostIn');
        this.ghostInForm = new FormGroup({
            username: new FormControl<string>('', [emailValidator()])
        });
        this.classicGhostInRoute = `${this.configService.getConfig().apiUrl}/ghostSession/neptune`;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.ghostInForm.get('username').valueChanges.pipe(debounceTime(1000)).subscribe(changes => {
                if (this.username.errors) {
                    this.showErrorMessages = true;
                } else {
                    this.showErrorMessages = false;
                }
            })
        );
    }

    get username(): FormControl {
        return this.ghostInForm.get('username') as FormControl;
    }

    ghostIn() {
        if (!this.username.value.length) {
            return;
        }
        this.loading = true;
        const usernameInput = this.username.value;

        this.loginApi.ghostIn({ username: usernameInput }).pipe(
            catchError(err => {
                this.loading = false;
                this.invalidGhostIn = true;
                return throwError(err);
            })
        ).subscribe({
            next: (token) => {
                this.loading = false;
                this.currentUserStateService.clearState();
                this.currentUserStateService.loadUserState();
                this.currentUserStateService.ignoreWebsockets = true;
                this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login/ghostin', HttpVerbsEnum.POST);
                this.websocketAwareNeptuneService.disconnectFromWebsocket();
                this.websocketAwareNeptuneService.connectToWebsocket();

                this.router.navigate(['Dashboard']);
                this.closeDialog();
                // @HACK: refresh the page after redirect, we do this because things don't handle refreshing data properly on loadUserState,
                // and localizations don't update either
                requestAnimationFrame(() => {
                    window.location.reload();
                });
            },
            error: () => {
                this.loading = false;
                this.invalidGhostIn = true;
            }
        });
    }
}
